import { createThemeToggleButton } from './ui/theme';
import { tabIcons } from './ui/icons';
import { createLangToggleButton, t } from './ui/lang';
import { esc } from './ui/modal';
import { appState, loadDeviceNames, loadFarmScreenOff } from './core/state';
import { can } from './core/access';
import { roleLabel } from './users/users-shared';
import { installRouteListener, syncRouteToHash, renderRoute, parseRouteFromHash, navigate } from './core/router';
import { onDevicesChange, renderFarmDevices } from './farms/farms-view';
import { renderClickerFarmDevices } from './clicker/clicker-view';
import { DeviceList } from './stream/device-list';
import { loadWorkspace } from './workspace/workspace-store';
import { renderWorkspace } from './workspace/workspace-view';
async function logout() {
    await fetch('/api/auth/logout', { method: 'POST' });
    location.reload();
}
export function bootstrap(user) {
    appState.currentUserRole = user.role;
    appState.currentUserLogin = user.login;
    appState.currentUserId = user.id ?? null;
    appState.currentUserTeamId = user.team_id ?? null;
    appState.currentUserTeamName = user.team_name ?? null;
    // "My Devices" leads the menu: it is the operator's home board, the one page that
    // shows work from every farm at once. The rest of the menu is unchanged.
    const workspaceNav = can('nav.workspace')
        ? `<a class="sidebar__item" id="nav-workspace" href="#">
               <span class="sidebar__item-icon">
                   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                     <path d="M4.5 2.2h9v13.6L9 12.4l-4.5 3.4z"/>
                   </svg>
               </span>
               <span class="sidebar__item-text">${t('nav.my_devices')}</span>
           </a>`
        : '';
    // Dashboards — the cross-farm reporting section (operations registry first).
    // Senior operator and above; the registry only ever shows farms the viewer can reach.
    const dashboardsNav = can('nav.dashboards')
        ? `<a class="sidebar__item" id="nav-dashboards" href="#">
               <span class="sidebar__item-icon">
                   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                     <rect x="2" y="2.5" width="14" height="13" rx="1.6"/>
                     <path d="M2 6.5h14M7 6.5v9"/>
                   </svg>
               </span>
               <span class="sidebar__item-text">${t('nav.dashboards')}</span>
           </a>`
        : '';
    // Charts — aggregates over the operations registry; admin and above.
    const chartsNav = can('nav.charts')
        ? `<a class="sidebar__item" id="nav-charts" href="#">
               <span class="sidebar__item-icon">
                   <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                     <path d="M2.5 2.5v13h13"/>
                     <path d="M5 12l3.2-3.6 2.6 2.2L15 5.5"/>
                   </svg>
               </span>
               <span class="sidebar__item-text">${t('nav.charts')}</span>
           </a>`
        : '';
    const clickerNav = can('nav.clicker')
        ? `<a class="sidebar__item" id="nav-clicker" href="#">
               <span class="sidebar__item-icon">
                   <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                     <path d="M5 2.5 5 11.5 7.4 9.3 9 13 11 12 9.3 8.4 12.5 8 5 2.5Z" fill="currentColor"/>
                   </svg>
               </span>
               <span class="sidebar__item-text">${t('nav.clicker')}</span>
           </a>`
        : '';
    // Flows — panel-authored clicker flows. Its own section rather than a clicker-settings
    // tab: the editor is a full page, not a modal, and it is reached straight from the menu.
    const flowsNav = can('nav.flows')
        ? `<a class="sidebar__item" id="nav-flows" href="#">
               <span class="sidebar__item-icon sidebar__item-icon--stroke">${tabIcons.flows}</span>
               <span class="sidebar__item-text">${t('nav.flows')}</span>
           </a>`
        : '';
    const adminNav = can('nav.users')
        ? `<a class="sidebar__item" id="nav-users" href="#">
               <span class="sidebar__item-icon">👥</span>
               <span class="sidebar__item-text">${t('nav.users')}</span>
           </a>`
        : '';
    const adbNav = can('page.adbProxy')
        ? `<a class="sidebar__item" id="nav-adb" href="#">
               <span class="sidebar__item-icon">🔌</span>
               <span class="sidebar__item-text">${t('nav.remote_adb')}</span>
           </a>`
        : '';
    // Superadmin-only sections (Clicker, Flows, Remote ADB) sit in their own group pinned
    // to the bottom of the menu, apart from the everyday items above; omitted entirely
    // for everyone else so the divider never shows over an empty group.
    const superadminItems = [clickerNav, flowsNav, adbNav].filter(Boolean);
    const superadminGroup = superadminItems.length > 0
        ? `<div class="sidebar__group sidebar__group--bottom">${superadminItems.join('')}</div>`
        : '';
    document.body.innerHTML = `
        <div class="app">
            <aside class="sidebar" id="sidebar">
                <div class="sidebar__logo">
                    <span class="sidebar__logo-icon">📱</span>
                    <span class="sidebar__logo-text">Mobile Farm</span>
                </div>
                <nav class="sidebar__menu">
                    <div class="sidebar__group">
                        ${workspaceNav}
                        ${dashboardsNav}
                        ${chartsNav}
                        <a class="sidebar__item" id="nav-farms" href="#">
                            <span class="sidebar__item-icon">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                  <rect x="10" y="0" width="6" height="11" rx="1.2" fill="currentColor" opacity="0.4"/>
                                  <rect x="6" y="3" width="6" height="11" rx="1.2" fill="currentColor" opacity="0.7"/>
                                  <rect x="2" y="6" width="6" height="11" rx="1.2" fill="currentColor"/>
                                </svg>
                            </span>
                            <span class="sidebar__item-text">${t('nav.farms')}</span>
                        </a>
                        ${adminNav}
                    </div>
                    ${superadminGroup}
                </nav>
                <div class="sidebar__footer">
                    <button class="sidebar__item logout-item" id="logout-btn">
                        <span class="sidebar__item-icon">↩</span>
                        <span class="sidebar__item-text">${t('nav.sign_out')}</span>
                    </button>
                </div>
            </aside>

            <div class="main-wrapper" id="main-wrapper">
                <header class="topnav">
                    <button class="topnav__toggle" id="sidebar-toggle" title="${t('nav.toggle_sidebar')}">☰</button>
                    <h1 class="topnav__title" id="page-title">${t('nav.farms')}</h1>
                    <div class="topnav__right">
                        <button class="topnav__lang-toggle" id="lang-toggle-btn" data-lang-toggle="1" type="button"></button>
                        <button class="topnav__theme-toggle" id="theme-toggle-btn" data-theme-toggle="1" type="button"></button>
                        <div class="user-avatar-wrap" id="user-avatar-wrap">
                            <button class="user-avatar" id="user-avatar-btn" title="${esc(user.login)}">${esc(user.login[0]?.toUpperCase() ?? 'U')}</button>
                            <div class="user-menu hidden" id="user-menu">
                                <div class="user-menu__header">
                                    <div class="user-menu__name">${esc(user.login)}</div>
                                    <div class="user-menu__role">${esc(roleLabel(user.role))}</div>
                                    <div class="user-menu__team" id="user-menu-team"${appState.currentUserTeamName ? '' : ' style="display:none"'}>${appState.currentUserTeamName ? esc(appState.currentUserTeamName) : ''}</div>
                                </div>
                                <div class="user-menu__divider"></div>
                                <button class="user-menu__item" id="menu-profile-btn">👤 ${t('nav.profile')}</button>
                                <button class="user-menu__item user-menu__item--danger" id="menu-logout-btn">↩ ${t('nav.sign_out')}</button>
                            </div>
                        </div>
                    </div>
                </header>
                <div class="content" id="content"></div>
            </div>
        </div>

    `;
    // Sidebar toggle
    const sidebar = document.getElementById('sidebar');
    const mainWrapper = document.getElementById('main-wrapper');
    const themeBtn = document.getElementById('theme-toggle-btn');
    if (themeBtn) {
        const replacement = createThemeToggleButton('topnav__theme-toggle');
        themeBtn.replaceWith(replacement);
    }
    const langBtn = document.getElementById('lang-toggle-btn');
    if (langBtn) {
        langBtn.replaceWith(createLangToggleButton('topnav__lang-toggle'));
    }
    document.getElementById('sidebar-toggle').addEventListener('click', () => {
        sidebar.classList.toggle('closed');
        mainWrapper.classList.toggle('sidebar-closed');
    });
    document.getElementById('logout-btn').addEventListener('click', () => void logout());
    // User avatar dropdown
    const avatarBtn = document.getElementById('user-avatar-btn');
    const userMenu = document.getElementById('user-menu');
    avatarBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        userMenu.classList.toggle('hidden');
    });
    document.addEventListener('click', () => userMenu.classList.add('hidden'));
    // The login/TOTP path doesn't carry the group or the user id; if either is still
    // unknown, pull them from /api/auth/me (reveals the group line, and supplies the
    // id used to lock self-edits like changing one's own role).
    if (appState.currentUserTeamName === null || appState.currentUserId === null) {
        void fetch('/api/auth/me').then(async (r) => {
            if (!r.ok)
                return;
            const me = await r.json();
            if (me.id !== undefined)
                appState.currentUserId = me.id;
            appState.currentUserTeamId = me.team_id ?? null;
            appState.currentUserTeamName = me.team_name ?? null;
            const teamEl = document.getElementById('user-menu-team');
            if (teamEl && appState.currentUserTeamName) {
                teamEl.textContent = appState.currentUserTeamName;
                teamEl.style.display = '';
            }
        }).catch(() => { });
    }
    document.getElementById('menu-logout-btn').addEventListener('click', () => void logout());
    document.getElementById('menu-profile-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        // Profile stub — no-op for now
    });
    // Nav: My Devices
    document.getElementById('nav-workspace')?.addEventListener('click', (e) => {
        e.preventDefault();
        navigate({ page: 'workspace' });
    });
    // Nav: Farms
    document.getElementById('nav-farms').addEventListener('click', (e) => {
        e.preventDefault();
        navigate({ page: 'farms' });
    });
    // Nav: Clicker (superadmin only)
    document.getElementById('nav-clicker')?.addEventListener('click', (e) => {
        e.preventDefault();
        navigate({ page: 'clicker' });
    });
    // Nav: Flows (superadmin only)
    document.getElementById('nav-flows')?.addEventListener('click', (e) => {
        e.preventDefault();
        navigate({ page: 'flows' });
    });
    // Nav: Dashboards (senior operator and above)
    document.getElementById('nav-dashboards')?.addEventListener('click', (e) => {
        e.preventDefault();
        navigate({ page: 'dashboards' });
    });
    // Nav: Charts (admin and above)
    document.getElementById('nav-charts')?.addEventListener('click', (e) => {
        e.preventDefault();
        navigate({ page: 'charts' });
    });
    // Nav: Users (admin only)
    const navUsers = document.getElementById('nav-users');
    if (navUsers) {
        navUsers.addEventListener('click', (e) => {
            e.preventDefault();
            navigate({ page: 'users' });
        });
    }
    // Nav: Remote ADB (superadmin only)
    const navAdb = document.getElementById('nav-adb');
    if (navAdb) {
        navAdb.addEventListener('click', (e) => {
            e.preventDefault();
            navigate({ page: 'adbProxy' });
        });
    }
    installRouteListener();
    syncRouteToHash();
    renderRoute(parseRouteFromHash());
    // Custom device names are stored server-side; load them once, then re-render
    // the open per-farm view so cards/rows pick up aliases that arrived before the
    // fetch resolved. Dispatch by route: a clicker/farm route also sets
    // appState.selectedFarm, so an unconditional renderFarmDevices() here clobbered
    // the clicker page (and the farm-settings page) with the farm stream grid on
    // refresh. Mirror the route guard onDevicesChange uses.
    void loadDeviceNames().then(() => {
        const route = parseRouteFromHash();
        if (route.page === 'workspace') {
            void renderWorkspace();
        }
        else if (route.page === 'farms' && route.farmId != null && !route.settings) {
            renderFarmDevices();
        }
        else if (route.page === 'clicker' && route.farmId != null) {
            void renderClickerFarmDevices(route.farmId);
        }
    });
    // Per-farm screen-off-on-connect preference; the inline-stream connect path
    // reads it synchronously, so load it once up front (deep-links don't hit the
    // farm-list view that would otherwise populate it).
    void loadFarmScreenOff();
    // The My Devices claim set, so every card's bookmark button renders in the right
    // state from the first paint instead of flipping once the dashboard is opened.
    void loadWorkspace();
    const list = new DeviceList(onDevicesChange);
    window.addEventListener('beforeunload', () => list.destroy());
    startSessionHeartbeat();
}
// Poll the session periodically so a superseded session (e.g. an operator kicked by a
// login elsewhere — one active session per operator) is bounced to the login screen
// even though the live device-list WebSocket, authenticated once at connect, stays open.
// Only an explicit 401 logs out; transient network errors are ignored so a blip doesn't
// kick a valid session.
function startSessionHeartbeat() {
    setInterval(() => {
        void fetch('/api/auth/me').then((res) => {
            if (res.status === 401)
                location.reload(); // → init() → renderLogin
        }).catch(() => { });
    }, 20000);
}
